import UIComponent from "../ui/component";

let telegramWidgetLoadPromise = null;

export class Auth extends UIComponent {
    init() {
        this.authButtons = Array.from(
            this.element.querySelectorAll(".js-telegram-auth-btn")
        );
        this.widgetConfig = document.querySelector(".js-telegram-widget-config");
        this.errorBox = this.element.querySelector(".js-telegram-auth-error");

        if (!this.authButtons.length) {
            return;
        }

        this.bindTelegramButtons();
        this.setButtonsLoadingState(true);
        this.preloadTelegramWidget();
    }

    bindTelegramButtons() {
        this.authButtons.forEach((button) => {
            button.addEventListener("click", async (evt) => {
                evt.preventDefault();
                await this.startTelegramAuth();
            });
        });
    }

    preloadTelegramWidget() {
        const preload = async () => {
            const isReady = await this.ensureTelegramReady();
            this.setButtonsLoadingState(!isReady);
        };

        if (typeof window.requestIdleCallback === "function") {
            window.requestIdleCallback(preload, { timeout: 1000 });
            return;
        }

        window.setTimeout(preload, 0);
    }

    async ensureTelegramReady() {
        if (this.isTelegramSdkReady()) {
            this.hideError();
            return true;
        }

        if (!this.widgetConfig) {
            this.showError(this.getTelegramUnavailableMessage());
            return false;
        }

        try {
            await this.loadTelegramWidgetOnce();
            this.hideError();
            return this.isTelegramSdkReady();
        } catch (error) {
            this.showError(this.getTelegramUnavailableMessage());
            return false;
        }
    }

    async loadTelegramWidgetOnce() {
        if (this.isTelegramSdkReady()) {
            return;
        }

        if (telegramWidgetLoadPromise) {
            return telegramWidgetLoadPromise;
        }

        const widgetSrc = this.widgetConfig?.dataset?.widgetSrc;
        if (!widgetSrc) {
            throw new Error("telegram_widget_src_missing");
        }

        telegramWidgetLoadPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            const timeoutMs = 4000;
            const timeoutId = window.setTimeout(() => {
                reject(new Error("telegram_widget_timeout"));
            }, timeoutMs);

            script.async = true;
            script.src = widgetSrc;
            script.className = "js-telegram-widget-sdk";

            script.onload = () => {
                window.clearTimeout(timeoutId);
                if (!this.isTelegramSdkReady()) {
                    reject(new Error("telegram_sdk_not_ready"));
                    return;
                }
                resolve();
            };

            script.onerror = () => {
                window.clearTimeout(timeoutId);
                reject(new Error("telegram_widget_load_failed"));
            };

            document.head.appendChild(script);
        }).catch((error) => {
            telegramWidgetLoadPromise = null;
            throw error;
        });

        return telegramWidgetLoadPromise;
    }

    async startTelegramAuth() {
        this.setButtonsLoadingState(true);
        const isReady = await this.ensureTelegramReady();
        if (!isReady) {
            this.setButtonsLoadingState(true);
            return;
        }

        const botId = this.widgetConfig?.dataset?.telegramBotId;
        const authUrl = this.widgetConfig?.dataset?.authUrl;

        if (!botId || !authUrl) {
            this.showError(this.getTelegramUnavailableMessage());
            this.setButtonsLoadingState(true);
            return;
        }

        window.Telegram.Login.auth(
            {
                bot_id: botId,
                request_access: true,
            },
            (data) => {
                if (!data) {
                    this.setButtonsLoadingState(false);
                    return;
                }

                const callbackUrl = new URL(authUrl, window.location.origin);
                callbackUrl.search = this.serialize(data);
                window.location.href = callbackUrl.toString();
            }
        );
    }

    isTelegramSdkReady() {
        return Boolean(window.Telegram?.Login?.auth);
    }

    setButtonsLoadingState(isLoading) {
        this.authButtons.forEach((button) => {
            if (isLoading) {
                button.classList.add("disabled");
                button.setAttribute("aria-disabled", "true");
                button.style.pointerEvents = "none";
                return;
            }

            button.classList.remove("disabled");
            button.removeAttribute("aria-disabled");
            button.style.pointerEvents = "";
        });
    }

    showError(message) {
        if (!this.errorBox) {
            return;
        }

        this.errorBox.textContent = message;
        this.errorBox.classList.remove("d-none");
    }

    hideError() {
        if (!this.errorBox) {
            return;
        }

        this.errorBox.textContent = "";
        this.errorBox.classList.add("d-none");
    }

    getTelegramUnavailableMessage() {
        const locale = window.app?.currentLocale;
        const fallback = "Telegram недоступен в вашей сети";
        return (
            window.app?.translation?.[locale]?.errors?.connection_refused ||
            fallback
        );
    }

    serialize(obj) {
        const str = [];

        for (const p in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        }

        return str.join("&");
    }
}
